/**
 * These groups correspond to the top level grouping of directories in the new navigation.
 */
export const DirectoryGroupConsts = {
  activeDirectory: 'active-directory',
  cloudDirectories: 'cloud-directories',
  hrDirectories: 'hr-directories',
};

export const DirectoryScopes = {
  user: 'https://www.googleapis.com/auth/admin.directory.user',
  group: 'https://www.googleapis.com/auth/admin.directory.group',
  domain: 'https://www.googleapis.com/auth/admin.directory.domain.readonly',
};

export const DirectoryTypeKeys = {
  activeDirectory: 'activeDirectory',
  bambooHR: 'bambooHR',
  bob: 'bob',
  gSuite: 'gSuite',
  ldapServer: 'ldapServer',
  namely: 'namely',
  office365: 'office365',
  paylocity: 'paylocity',
  personio: 'personio',
  okta: 'okta',
  workday: 'workday',
  deel: 'deel',
  factorial: 'factorial',
  dayforce: 'ceridiandayforcehcm',
};

export const DirectoryAppsTemplatesNames = [
  DirectoryTypeKeys.namely,
  DirectoryTypeKeys.paylocity,
  DirectoryTypeKeys.personio,
  DirectoryTypeKeys.dayforce,
];

export const DirectoryTypeConsts = {
  [DirectoryTypeKeys.activeDirectory]: 'active_directory',
  [DirectoryTypeKeys.bambooHR]: 'bambooHR',
  [DirectoryTypeKeys.gSuite]: 'g_suite',
  [DirectoryTypeKeys.ldapServer]: 'ldap_server',
  [DirectoryTypeKeys.office365]: 'office_365',
  [DirectoryTypeKeys.okta]: 'okta',
  [DirectoryTypeKeys.workday]: 'workday',
  [DirectoryTypeKeys.deel]: 'deel',
  [DirectoryTypeKeys.factorial]: 'factorial',
};

// NOTE: This is referenced by the underscore delimited directory type value
// seen in DirectoryTypeConsts because this field is returned in each
// directory record returned from the server. It is used to ensure we display
// the proper name for the directory in the UI, e.g. Google Workspace instead of Google
// Apps.
export const DirectoryNameConsts = {
  [DirectoryTypeConsts.activeDirectory]: 'Active Directory Domain',
  [DirectoryTypeKeys.bambooHR]: 'BambooHR',
  [DirectoryTypeConsts.bob]: 'bob',
  [DirectoryTypeConsts.deel]: 'Deel',
  [DirectoryTypeConsts.factorial]: 'Factorial',
  [DirectoryTypeConsts.gSuite]: 'Google Workspace',
  [DirectoryTypeKeys.namely]: 'Namely',
  [DirectoryTypeConsts.ldapServer]: 'JumpCloud LDAP',
  [DirectoryTypeConsts.office365]: 'M365/Azure AD',
  [DirectoryTypeConsts.okta]: 'Okta',
  [DirectoryTypeKeys.paylocity]: 'Paylocity',
  [DirectoryTypeKeys.personio]: 'Personio',
  [DirectoryTypeConsts.workday]: 'Workday Import',
  [DirectoryTypeKeys.dayforce]: 'Ceridian Dayforce HCM',
};

export const AdBridgeAgentStates = {
  active: 'active',
  inactive: 'inactive',
  unsealed: 'unsealed',
};

export const AdBridgeAgentTypes = {
  importAgent: 'importAgent',
  syncAgent: 'syncAgent',
};

export const DirectoryActionConsts = {
  activated: 'activated',
  cloudDirectoryReactivated: 'cloudDirectoryReactivated',
  create: 'create',
  currentState: 'currentState',
  details: 'details',
  groupsEnabled: 'groupsEnabled',
  error: 'error',
  import_users: 'import-users',
  reactivated: 'reactivated',
  scopes: 'scopes',
  tabIndex: 'tabIndex',
};

export const gSuiteConstants = {
  errorMap: {
    INVALID_ARGUMENT: {
      message: 'Unable to retrieve domains.',
      saveMessage: 'Invalid domain selected. Select a valid domain and try again.',
      default: 'There was a problem with the domains update.  Try again.',
    },
    SOURCEDOMAIN_ERROR: {
      message: 'Unable to load domains from google workspace',
    },
    PERMISSION_DENIED: 'Permission Denied',
  },
};

export const o365ApiConstants = {
  consistencyLevel: 'ConsistencyLevel',
  eventual: 'eventual',
  errorMap: {
    INVALID_ARGUMENT: {
      message: 'Unable to retrieve domains.',
      saveMessage: 'Invalid domain selected. Select a valid domain and try again.',
      default: 'There was a problem with the domains update.  Try again.',
    },
    SOURCEDOMAIN_ERROR: {
      message: 'Unable to load domains from M365/AAD',
    },
  },
};

export const o365Fields = {
  givenName: 'givenName',
  surname: 'surname',
  userPrincipalName: 'userPrincipalName',
};

export const DirectoryAttributeDirectionConsts = {
  export: {
    value: 'export',
    name: 'Export',
  },
  import: {
    value: 'import',
    name: 'Import',
  },
  exclude: {
    value: 'exclude',
    name: 'Exclude',
  },
};

export const DirectoryUserPrincipalNameFromAlternateEmail = {
  companyEmail: {
    value: 'companyEmail',
    name: 'Company Email',
  },
  alternateEmail: {
    value: 'alternateEmail',
    name: 'Alternate Email',
  },
};

export const GSuiteAttributesConsts = [
  {
    direction: 'export',
    ownedByJC: true,
    id: 'firstname',
    jc: 'firstname',
    directory: 'name.givenName',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: true,
    id: 'lastname',
    jc: 'lastname',
    directory: 'name.familyName',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: true,
    id: 'password',
    jc: 'password',
    directory: 'password',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: true,
    id: 'email',
    jc: 'email',
    directory: 'primaryEmail',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_home_addresses',
    jc: 'addresses[type=home]',
    directory: 'addresses[type=home]',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_home_phone_numbers',
    jc: 'phoneNumbers[type=home]',
    directory: 'phones[type=home]',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_mobile_phone_numbers',
    jc: 'phoneNumbers[type=mobile]',
    directory: 'phones[type=mobile]',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_work_addresses',
    jc: 'addresses[type=work]',
    directory: 'addresses[type=work]',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_work_phone_numbers',
    jc: 'phoneNumbers[type=work]',
    directory: 'phones[type=work]',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_work_fax_phone_numbers',
    jc: 'phoneNumbers[type=work_fax]',
    directory: 'phones[type=work_fax]',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_work_mobile_phone_numbers',
    jc: 'phoneNumbers[type=work_mobile]',
    directory: 'phones[type=work_mobile]',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_primary_organization_description',
    jc: 'employeeType',
    directory: 'primaryOrganization.description',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_primary_organization_department',
    jc: 'department',
    directory: 'primaryOrganization.department',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_primary_organization_employee_id',
    jc: 'employeeIdentifier',
    directory: 'externalIds[]',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_primary_organization_cost_center',
    jc: 'costCenter',
    directory: 'primaryOrganization.costCenter',
    status: true,
  },
  {
    direction: 'export',
    ownedByJC: false,
    id: 'user_primary_organization_title',
    jc: 'jobTitle',
    directory: 'primaryOrganization.title',
    status: true,
  },
];
